/*
 * 時刻・経過時間 に対するHuman Read/Writable な表現から、内部表現に変換する
 *
 * ## 時刻
 *
 * ### 受け付ける形式
 * * HH:MM (出力時の標準)
 * * H:MM
 * * HHMM
 * * HMM
 *
 * ### 内部形式
 * HH:MM
 *
 *
 * ## 経過時間
 *
 * ### 受け付ける形式
 * * HH:MM (出力時の標準)
 * * H:MM
 * * HHMM
 * * HMM
 * * H
 * * .H
 * * H.H
 *
 * ### 内部形式
 * Duration
 *
 */

import { z } from 'zod'
import * as duration from 'duration-fns'

/********************************************
 * for TIME
 ********************************************/

const _timeHHMM = Symbol('timeHHMM') // never export this

// HH:MM or H:MM or HHMM or HMM 形式を標準的なHH:MM形式に正規化
// 例:
// - "9:30" → "09:30" (一桁の時間を二桁に)
// - "12:45" → "12:45" (既に正しい形式はそのまま)
// - "930" → "9:30" (区切りなしの場合、コロンを挿入)
// - "1245" → "12:45" (四桁の場合、前半を時間、後半を分として解釈)
const normalizeHHMM = (v: string): string => {
  if (v.includes(':')) {
    // HH:MM or H:MM
    return v.split(':')[0].length === 1 ? '0' + v : v
  } else {
    // HHMM or HMM
    return v.substring(0, v.length - 2) + ':' + v.substring(v.length - 2)
  }
}

// HH:MM or H:MM or HHMM or HMM format
const hhmmNotation = z.string().regex(/^[0-9]{1,2}:?[0-5][0-9]$/)

export const timeNotation = hhmmNotation.transform(normalizeHHMM).brand(_timeHHMM)

export const timeHHMM = timeNotation.parse

// 入力文字列を時間形式に安全に変換し、失敗した場合は元の値を返す
// 例:
// - "9:30" → "09:30" (正規化に成功)
// - "12:45" → "12:45" (既に正しい形式はそのまま)
// - "25:00" → "25:00" (標準時間範囲外だが形式が正しければ処理)
// - "abc" → "abc" (パース不可能な場合は元の値を返す)
export const tryNormalizedTime = (v: string): string => {
  const result = timeNotation.safeParse(v)
  if (!result.success) return v
  return result.data
}

/********************************************
 * for Duration
 ********************************************/

const _durationHHMM = Symbol('durationHHMM') // never export this

// HH:MM or H:MM or HMM or HHMM 形式を Duration オブジェクトに変換
// 例:
// - "8:30" → { hours: 8, minutes: 30 }
// - "09:15" → { hours: 9, minutes: 15 }
// - "1:45" → { hours: 1, minutes: 45 }
// - "930" → { hours: 9, minutes: 30 } (区切りなしも変換)
// - "1230" → { hours: 12, minutes: 30 } (区切りなしも変換)
const hhmmToDuration = (v: string): Duration => {
  const [hh, mm] = normalizeHHMM(v).split(':')
  return { hours: Number(hh), minutes: Number(mm) }
}

// H.H + h 形式を Duration オブジェクトに変換
// 例:
// - "1.5h" → { hours: 1, minutes: 30 } (1.5時間)
// - "2.25h" → { hours: 2, minutes: 15 } (2.25時間)
// - "0.5h" → { hours: 0, minutes: 30 } (0.5時間)
// - "8h" → { hours: 8, minutes: 0 } (8時間)
// 注: 100時間以上の場合はエラー
const hoursToDuration = (v: string, ctx: z.RefinementCtx): Duration => {
  const n = Number(v.substring(0, v.length - 1))
  if (n >= 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '100時間未満で入力してください'
    })
    return z.NEVER
  }
  const hours = Math.trunc(n)
  const minutes = Math.trunc((n - hours) * 60)
  return { hours, minutes }
}

// M + m 形式を Duration オブジェクトに変換
// 例:
// - "90m" → { minutes: 90 } (90分)
// - "30m" → { minutes: 30 } (30分)
// - "120m" → { minutes: 120 } (120分 = 2時間)
// - "45m" → { minutes: 45 } (45分)
// 注: 6000分（100時間）以上の場合はエラー
const minutesToDuration = (v: string, ctx: z.RefinementCtx): Duration => {
  const minutes = Number(v.substring(0, v.length - 1))
  if (minutes >= 100 * 60) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '100時間未満で入力してください'
    })
    return z.NEVER
  }
  return { minutes }
}

const decimalHoursNotation = z.string().regex(/^(\d+)?\.?(\d+)?h/)
const integerMinutesNotation = z.string().regex(/^\d+m/)

export const durationNotation = z
  .union([
    timeNotation.transform(hhmmToDuration),
    decimalHoursNotation.transform(hoursToDuration),
    integerMinutesNotation.transform(minutesToDuration)
  ])
  .brand(_durationHHMM)

export const durationHHMM = durationNotation.parse

// 入力文字列を期間形式に安全に変換し、失敗した場合は元の値を返す
// 例:
// - "1:30" → "1:30" (既に正しい形式はそのまま)
// - "1.5h" → "1:30" (1.5時間を時:分形式に変換)
// - "90m" → "1:30" (90分を時:分形式に変換)
// - "abc" → "abc" (パース不可能な場合は元の値を返す)
export const tryNormalizedDuration = (v: string): string => {
  const result = durationNotation.safeParse(v)
  if (!result.success) return v
  return durationToHHMM(result.data)
}

// Duration オブジェクトを HH:MM 形式の文字列に変換
// 例:
// - { hours: 8, minutes: 30 } → "8:30"
// - { hours: 1, minutes: 5 } → "1:05"
// - { hours: 0, minutes: 45 } → "0:45"
// - { minutes: 90 } → "1:30" (正規化が行われる)
export const durationToHHMM = (d: Duration): string => {
  const nd = duration.normalize(d)
  const hourPart = Math.trunc(duration.toHours(nd))
  return String(hourPart) + ':' + String(nd.minutes).padStart(2, '0')
}

/**
 * 時間入力値を正規化し、35時間を超える場合は tryNormalizedTime を使用
 * @param value 入力された時間文字列
 * @returns 正規化された時間文字列
 */
// 全角数字とコロンを半角に変換
// 例:
// - "１２：３０" → "12:30"
// - "０９：１５" → "09:15"
// - "８：４５" → "8:45"
// - "２３：００" → "23:00"
const convertFullWidthToHalfWidth = (str: string): string => {
  return str
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/：/g, ':')
}

/**
 * 時刻文字列を正規化し、35時間を超える場合は tryNormalizedTime を使用
 * @param value 入力された時間文字列
 * @returns 正規化された時間文字列
 *
 * 例:
 * - "12" → "12:00" (数字のみの場合、時間として扱い分は00に)
 * - "9" → "09:00" (一桁の場合、二桁に整形し分は00に)
 * - "930" → "09:30" (三桁または四桁の場合、HH:MM形式に)
 * - "１２：３０" → "12:30" (全角文字を半角に変換)
 * - "36:00" → "36:00" (35時間超過の場合、tryNormalizedTimeで処理)
 * - "1.5h" → "01:30" (時間表記を正規化)
 */
export function normalizeTimeValue(value: string): string {
  // 1. 入力値の整理と全角文字の変換
  const input = convertFullWidthToHalfWidth(value.trim())

  // 2. 数字のみの場合の処理
  if (/^\d+$/.test(input)) {
    const num = parseInt(input)
    const formatted = num.toString().padStart(2, '0') + ':00'

    // 35時間制限のチェック
    if (num > 35) {
      return tryNormalizedTime(formatted)
    }
    return formatted
  }

  // 3. Duration として正規化を試みる
  const normalized = tryNormalizedDuration(input)
  const hoursPart = parseInt(normalized.split(':')[0])

  // 4. 時間制限のチェックと最終的な正規化
  if (hoursPart > 35) {
    return tryNormalizedTime(input)
  }

  return normalized
}
